import { useCallback, useEffect, useMemo, useState } from 'react';
import { cardStyle, eyebrowStyle, ghostLinkStyle, inputStyle, pageStyle } from '@kilat/ui';
import { Link } from 'react-router-dom';

import { useAuth } from '../lib/use-auth.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { logger } from '../lib/logger.js';
import { PAGE_FRAME_MAX, dashboardPageStyle, sectionGridStyle } from '../lib/layout.js';
import {
  callListIntegrations,
  callSetIntegrationConfig,
  callSetIntegrationSecret,
  type IntegrationItem,
} from '../lib/callables/integrations.js';

/**
 * /superadmin/integrations — ADR-0021. One panel to add/configure every
 * external service Kilat talks to (toyyibPay, Billplz, Llama AI, …) without
 * the gcloud CLI.
 *
 * Two-tier, per the ADR:
 *   - NON-SECRET settings (return URL, AI model/provider/TTL) → saved to
 *     Firestore, read live by the handlers.
 *   - SECRET values (API keys) → pushed to Secret Manager via callable; the
 *     value never round-trips back. The panel shows only "set ✓ / not set".
 *
 * Every state explicit per [[feedback-no-dead-ends]]: loading / gated / error
 * / empty / saving / saved / failed, with a clear next action on each.
 */

export function SuperadminIntegrationsPage() {
  const { claims, loading: authLoading } = useAuth();
  const brand = usePlatformBrand();
  const isSuper = useMemo(() => isSuperAdminForBrand(claims, brand.id), [claims, brand.id]);

  const [items, setItems] = useState<IntegrationItem[] | null>(null);
  const [loadState, setLoadState] = useState<
    { kind: 'loading' } | { kind: 'ready' } | { kind: 'error'; message: string }
  >({ kind: 'loading' });

  const load = useCallback(async () => {
    setLoadState({ kind: 'loading' });
    try {
      const res = await callListIntegrations(brand.id);
      setItems(res.items);
      setLoadState({ kind: 'ready' });
    } catch (err) {
      logger.error('integrations: load failed', { error: String(err) });
      setLoadState({ kind: 'error', message: friendlyError(err) });
    }
  }, [brand.id]);

  useEffect(() => {
    if (isSuper) void load();
  }, [isSuper, load]);

  if (authLoading) return <Screen title="Loading…" />;
  if (!isSuper) {
    return (
      <Screen
        title="Super-admin only"
        body={`Integrations is restricted to ${brand.name} super-admins. Sign in with a super-admin account, or head back to the queue.`}
        cta={{ label: 'Back to queue', to: '/queue' }}
      />
    );
  }

  return (
    <main style={dashboardPageStyle}>
      <header style={headerStyle}>
        <div style={{ maxWidth: PAGE_FRAME_MAX, margin: '0 auto', padding: 'var(--space-5)' }}>
          <p style={eyebrowStyle}>Super-admin · Integrations</p>
          <h1
            style={{ margin: 'var(--space-1) 0 0 0', fontSize: 'var(--text-h1)', fontWeight: 600 }}
          >
            External services
          </h1>
          <p
            style={{
              margin: 'var(--space-2) 0 0 0',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              maxWidth: 680,
            }}
          >
            Configure every external service from here — no command line. Settings save to the
            platform and apply live. API keys go straight to Secret Manager (never shown back) and
            take effect on the next request.
          </p>
          <div style={{ marginTop: 'var(--space-4)' }}>
            <Link to="/queue" style={ghostLinkStyle}>
              ← Back to queue
            </Link>
          </div>
        </div>
      </header>

      <section style={contentStyle}>
        {loadState.kind === 'loading' ? (
          <Card>
            <p style={{ margin: 0, color: 'var(--color-on-surface-muted)' }}>
              Loading integrations…
            </p>
          </Card>
        ) : null}

        {loadState.kind === 'error' ? (
          <Card>
            <p style={{ margin: 0, fontWeight: 600 }}>Couldn’t load integrations</p>
            <p
              style={{
                margin: 'var(--space-2) 0 var(--space-4) 0',
                color: 'var(--color-on-surface-muted)',
              }}
            >
              {loadState.message}
            </p>
            <button style={primaryBtnStyle} onClick={() => void load()}>
              Try again
            </button>
          </Card>
        ) : null}

        {loadState.kind === 'ready' && items
          ? items.map((item) => (
              <IntegrationCard key={item.service} item={item} brandId={brand.id} onChanged={load} />
            ))
          : null}
      </section>
    </main>
  );
}

// ── One service card ──────────────────────────────────────────────────────

function IntegrationCard({
  item,
  brandId,
  onChanged,
}: {
  readonly item: IntegrationItem;
  readonly brandId: string;
  readonly onChanged: () => Promise<void>;
}) {
  const [enabled, setEnabled] = useState(item.enabled);
  const [settings, setSettings] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const f of item.settingFields) {
      const v = item.settings[f.key];
      initial[f.key] = v === undefined ? '' : String(v);
    }
    return initial;
  });
  const [configStage, setConfigStage] = useState<Stage>({ kind: 'idle' });
  const [environment, setEnvironment] = useState<'sandbox' | 'production'>(
    item.environment ?? 'sandbox',
  );

  const saveConfig = useCallback(async () => {
    setConfigStage({ kind: 'saving' });
    try {
      const payload: Record<string, string | number | boolean> = {};
      for (const f of item.settingFields) {
        const raw = settings[f.key] ?? '';
        if (raw === '') continue; // unset → omit (handler keeps prior/env default)
        payload[f.key] = f.kind === 'number' ? Number(raw) : raw;
      }
      await callSetIntegrationConfig({
        platformBrandId: brandId,
        service: item.service,
        enabled,
        environment: item.hasEnvironment ? environment : undefined,
        settings: payload,
      });
      setConfigStage({ kind: 'saved' });
      setTimeout(() => setConfigStage({ kind: 'idle' }), 1400);
    } catch (err) {
      logger.error('integrations: save config failed', {
        service: item.service,
        error: String(err),
      });
      setConfigStage({ kind: 'failed', message: friendlyError(err) });
    }
  }, [
    brandId,
    enabled,
    environment,
    item.hasEnvironment,
    item.service,
    item.settingFields,
    settings,
  ]);

  return (
    <Card>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: 'var(--text-h3, 18px)', fontWeight: 600 }}>
            {item.label}
          </h2>
          <p
            style={{
              margin: 'var(--space-1) 0 0 0',
              color: 'var(--color-on-surface-muted)',
              fontSize: 'var(--text-meta)',
            }}
          >
            {item.summary}
          </p>
        </div>
        <StatusPill status={item.status} />
      </div>

      {/* Enabled toggle */}
      <label style={toggleRowStyle}>
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        <span>Enabled</span>
      </label>

      {/* Environment (sandbox / production) — for services that have it */}
      {item.hasEnvironment ? (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <label style={fieldLabelStyle}>Environment</label>
          <select
            style={inputStyle}
            value={environment}
            onChange={(e) => setEnvironment(e.target.value as 'sandbox' | 'production')}
          >
            <option value="sandbox">Sandbox — testing, fake money</option>
            <option value="production">Production — live, real money</option>
          </select>
          <p style={helpStyle}>
            Switch to Production only when you’ve registered the live account and set the production
            keys above.
          </p>
        </div>
      ) : null}

      {/* Non-secret settings */}
      {item.settingFields.length > 0 ? (
        <div style={{ marginTop: 'var(--space-4)' }}>
          <p style={subheadStyle}>Settings</p>
          {item.settingFields.map((f) => (
            <div key={f.key} style={{ marginBottom: 'var(--space-3)' }}>
              <label style={fieldLabelStyle}>
                {f.label}
                {f.required ? ' *' : ''}
              </label>
              {f.kind === 'enum' ? (
                <select
                  style={inputStyle}
                  value={settings[f.key] ?? ''}
                  onChange={(e) => setSettings((s) => ({ ...s, [f.key]: e.target.value }))}
                >
                  <option value="">(default)</option>
                  {(f.enumValues ?? []).map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  style={inputStyle}
                  type={f.kind === 'number' ? 'number' : 'text'}
                  inputMode={f.kind === 'number' ? 'numeric' : undefined}
                  placeholder={f.placeholder ?? ''}
                  value={settings[f.key] ?? ''}
                  onChange={(e) => setSettings((s) => ({ ...s, [f.key]: e.target.value }))}
                />
              )}
              {f.help ? <p style={helpStyle}>{f.help}</p> : null}
            </div>
          ))}
        </div>
      ) : null}

      {/* Save settings */}
      <div style={{ marginTop: 'var(--space-2)', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          style={primaryBtnStyle}
          disabled={configStage.kind === 'saving'}
          onClick={() => void saveConfig()}
        >
          {configStage.kind === 'saving' ? 'Saving…' : 'Save settings'}
        </button>
        {configStage.kind === 'saved' ? <span style={okStyle}>✓ Saved</span> : null}
        {configStage.kind === 'failed' ? <span style={errStyle}>{configStage.message}</span> : null}
      </div>

      {/* Secrets */}
      {item.secrets.length > 0 ? (
        <div
          style={{
            marginTop: 'var(--space-5)',
            borderTop: '1px solid var(--color-border)',
            paddingTop: 'var(--space-4)',
          }}
        >
          <p style={subheadStyle}>API keys &amp; secrets</p>
          <p style={{ ...helpStyle, marginTop: 0 }}>
            Stored in Secret Manager — never shown back here. A saved key applies on the next
            request.
          </p>
          {item.secrets.map((sec) => (
            <SecretRow
              key={sec.name}
              brandId={brandId}
              service={item.service}
              secret={sec}
              onSaved={onChanged}
            />
          ))}
        </div>
      ) : null}

      {item.runbook ? (
        <p style={{ ...helpStyle, marginTop: 'var(--space-4)' }}>
          Setup guide: <code style={codeStyle}>{item.runbook}</code>
        </p>
      ) : null}
    </Card>
  );
}

function SecretRow({
  brandId,
  service,
  secret,
  onSaved,
}: {
  readonly brandId: string;
  readonly service: string;
  readonly secret: IntegrationItem['secrets'][number];
  readonly onSaved: () => Promise<void>;
}) {
  const [value, setValue] = useState('');
  const [stage, setStage] = useState<Stage>({ kind: 'idle' });

  const save = useCallback(async () => {
    if (value.length === 0) return;
    setStage({ kind: 'saving' });
    try {
      await callSetIntegrationSecret({
        platformBrandId: brandId,
        service,
        secretName: secret.name,
        value,
      });
      setValue('');
      setStage({ kind: 'saved' });
      await onSaved(); // refresh "set ✓" status from server
      setTimeout(() => setStage({ kind: 'idle' }), 1400);
    } catch (err) {
      logger.error('integrations: save secret failed', {
        service,
        secret: secret.name,
        error: String(err),
      });
      setStage({ kind: 'failed', message: friendlyError(err) });
    }
  }, [brandId, onSaved, secret.name, service, value]);

  return (
    <div style={{ marginBottom: 'var(--space-3)' }}>
      <div
        style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}
      >
        <label style={fieldLabelStyle}>
          {secret.label}
          {secret.required ? ' *' : ''}
        </label>
        {secret.isSet ? (
          <span style={okStyle}>
            ✓ Set
            {secret.updatedAtMs ? ` · ${new Date(secret.updatedAtMs).toLocaleDateString()}` : ''}
          </span>
        ) : (
          <span style={{ ...helpStyle, margin: 0, color: 'var(--color-on-surface-muted)' }}>
            Not set
          </span>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          style={{ ...inputStyle, flex: 1 }}
          type="password"
          autoComplete="off"
          placeholder={secret.isSet ? 'Enter a new value to rotate' : 'Paste the key'}
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        <button
          style={secondaryBtnStyle}
          disabled={stage.kind === 'saving' || value.length === 0}
          onClick={() => void save()}
        >
          {stage.kind === 'saving' ? 'Saving…' : secret.isSet ? 'Rotate' : 'Save'}
        </button>
      </div>
      {stage.kind === 'saved' ? (
        <p style={{ ...okStyle, margin: 'var(--space-1) 0 0 0' }}>✓ Stored in Secret Manager</p>
      ) : null}
      {stage.kind === 'failed' ? (
        <p style={{ ...errStyle, margin: 'var(--space-1) 0 0 0' }}>{stage.message}</p>
      ) : null}
    </div>
  );
}

// ── Bits ───────────────────────────────────────────────────────────────────

type Stage =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved' }
  | { kind: 'failed'; message: string };

function StatusPill({ status }: { readonly status: 'live' | 'planned' }) {
  const live = status === 'live';
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: 0.4,
        textTransform: 'uppercase',
        padding: '3px 10px',
        borderRadius: 999,
        whiteSpace: 'nowrap',
        background: live ? 'rgba(13,122,79,0.10)' : 'var(--color-border)',
        color: live ? 'var(--color-primary)' : 'var(--color-on-surface-muted)',
      }}
    >
      {live ? 'Live' : 'Planned'}
    </span>
  );
}

function Card({ children }: { readonly children: React.ReactNode }) {
  return <div style={{ ...cardStyle, marginBottom: 'var(--space-5)' }}>{children}</div>;
}

function Screen({
  title,
  body,
  cta,
}: {
  readonly title: string;
  readonly body?: string;
  readonly cta?: { label: string; to: string };
}) {
  return (
    <main style={{ ...pageStyle, display: 'grid', placeItems: 'center', minHeight: '60vh' }}>
      <div style={{ textAlign: 'center', maxWidth: 480, padding: 'var(--space-6)' }}>
        <h1 style={{ margin: 0, fontSize: 'var(--text-h2)', fontWeight: 600 }}>{title}</h1>
        {body ? (
          <p
            style={{
              margin: 'var(--space-3) 0 0 0',
              color: 'var(--color-on-surface-muted)',
              lineHeight: 1.5,
            }}
          >
            {body}
          </p>
        ) : null}
        {cta ? (
          <Link
            to={cta.to}
            style={{
              display: 'inline-block',
              marginTop: 'var(--space-4)',
              padding: '12px 22px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-primary)',
              color: 'var(--color-primary-fg)',
              fontWeight: 500,
              textDecoration: 'none',
            }}
          >
            {cta.label}
          </Link>
        ) : null}
      </div>
    </main>
  );
}

function isSuperAdminForBrand(claims: Record<string, unknown> | null, brandId: string): boolean {
  if (!claims) return false;
  const sa = (claims as { superadmin?: Record<string, unknown> }).superadmin;
  if (!sa || typeof sa !== 'object') return false;
  return sa[brandId] === true;
}

function friendlyError(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  if (msg.includes('permission-denied')) return 'You’re not a super-admin for this brand.';
  if (msg.includes('failed-precondition')) {
    return 'The functions service account needs the Secret Manager Admin role. Grant it, then retry.';
  }
  if (msg.includes('unauthenticated')) return 'Your session expired — sign in again.';
  return msg.replace(/^.*?\/?\s*/, '') || 'Something went wrong. Try again.';
}

// ── Styles (mirrors superadmin-plans.tsx tokens) ────────────────────────────
const headerStyle: React.CSSProperties = {
  background: 'var(--color-surface)',
  borderBottom: '1px solid var(--color-border)',
};
/**
 * Service cards two (or three) abreast once each can have 27.5rem, one column on
 * a phone. Row spacing comes from each card's own bottom margin.
 */
const contentStyle: React.CSSProperties = {
  ...sectionGridStyle,
  rowGap: 0,
  maxWidth: PAGE_FRAME_MAX,
  margin: '0 auto',
  padding: 'var(--space-5) var(--space-5) var(--space-12) var(--space-5)',
};
const subheadStyle: React.CSSProperties = {
  margin: '0 0 var(--space-2) 0',
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: 'var(--color-on-surface-muted)',
};
const fieldLabelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 'var(--text-meta)',
  fontWeight: 500,
  marginBottom: 6,
};
const helpStyle: React.CSSProperties = {
  margin: '6px 0 0 0',
  fontSize: 12,
  color: 'var(--color-on-surface-muted)',
  lineHeight: 1.45,
};
const toggleRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginTop: 'var(--space-4)',
  fontSize: 'var(--text-meta)',
  fontWeight: 500,
};
const primaryBtnStyle: React.CSSProperties = {
  padding: '10px 18px',
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-primary)',
  color: 'var(--color-primary-fg)',
  border: 'none',
  fontWeight: 500,
  cursor: 'pointer',
};
const secondaryBtnStyle: React.CSSProperties = {
  padding: '10px 16px',
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-surface)',
  color: 'var(--color-on-surface)',
  border: '1px solid var(--color-border-strong)',
  fontWeight: 500,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};
const okStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--color-primary)',
};
const errStyle: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: '#b42318' };
const codeStyle: React.CSSProperties = {
  fontFamily: 'var(--font-mono, ui-monospace, monospace)',
  fontSize: 12,
  background: 'var(--color-border)',
  padding: '2px 6px',
  borderRadius: 6,
};
